/**
 * useGameLogger — her oyun aksiyonunu anında backend'e gönderir.
 *
 * Kullanım:
 *   const log = useGameLogger(gameId, userId);
 *   log({ type: 'place', row: 2, col: 4, value: 7, result: 'correct' });
 *
 * Backend'de backend/logs/game_actions.jsonl dosyasına satır satır eklenir.
 * Hata olursa sessizce görmezden gelinir — loglama oyunu bloke etmemeli.
 */

import { useCallback, useRef } from 'react';
import { type GameActionType, type GameAction } from '@sudoku/shared';

// Re-export so existing imports from this file keep working
export type { GameActionType, GameAction };

const API = import.meta.env.VITE_API_URL || 'http://localhost:8000';

export function useGameLogger(
  gameId: string | null,
  userId: string | null,
) {
  // session_id: bu tarayıcı sekmesindeki oyun seansını tanımlar
  const sessionIdRef = useRef<string>(crypto.randomUUID());

  const log = useCallback((action: GameAction) => {
    if (!gameId) return;

    const payload = {
      session_id: sessionIdRef.current,
      game_id: gameId,
      user_id: userId ?? 'anon',
      ts: Date.now(),
      ...action,
    };

    // Fire-and-forget — await yok, hata oyunu bloke etmez
    fetch(`${API}/debug/game-action`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      // keepalive: true sayesinde sekme kapanırken de gönderilmeye çalışılır
      keepalive: true,
    }).catch(() => {/* sessizce yut */});
  }, [gameId, userId]);

  // gameId değişince (yeni oyun başlayınca) session_id sıfırla
  const lastGameIdRef = useRef<string | null>(null);
  if (gameId !== lastGameIdRef.current) {
    lastGameIdRef.current = gameId;
    sessionIdRef.current = crypto.randomUUID();
  }

  return log;
}
